import { useState, useRef, useCallback, useEffect } from 'react';
import Webcam from 'react-webcam';
import { Camera, Upload as UploadIcon, ArrowRight, RefreshCw, Loader2 } from 'lucide-react';
import { authAPI } from '../../services/api';
import { useNavigate } from 'react-router-dom';
import JourneyLayout from '../../components/JourneyLayout';
import { eventToRouteMap } from '../../Utils/constants';

function UploadPicture() {
  const webcamRef = useRef(null);
  const fileInputRef = useRef(null);
  const [preview, setPreview] = useState(null);
  const [fileExt, setFileExt] = useState('jpeg');
  const [dragActive, setDragActive] = useState(false);
  const [error, setError] = useState('');
  const [uploading, setUploading] = useState(false);
  const [checkingAuth, setCheckingAuth] = useState(true);
  const navigate = useNavigate();

  useEffect(() => {
    const checkLoginStatus = async () => {
      setCheckingAuth(true);
      const token = localStorage.getItem('token') || 
                    localStorage.getItem('authToken') || 
                    localStorage.getItem('authtoken');
      const profileId = localStorage.getItem('profile_id') || '';
      
      if (!token || !profileId) {
        navigate('/apply-now');
        return;
      }

      try {
        await authAPI.getDashboardData(profileId);
      } catch (error) {
        console.error('Error fetching dashboard data:', error);
      } finally {
        setCheckingAuth(false);
      }
    };

    checkLoginStatus();
  }, [navigate]);

  const handleCapture = useCallback(() => {
    if (webcamRef.current) {
      const imageSrc = webcamRef.current.getScreenshot();
      if (imageSrc) {
        setPreview(imageSrc);
        setFileExt('jpeg');
        setError('');
      }
    }
  }, []);

  const handleRetake = () => {
    setPreview(null);
  };

  const handleFile = (file) => {
    if (!file) return;
    if (!file.type.startsWith('image/')) {
      setError('Please select a valid image file');
      return;
    }

    const reader = new FileReader();
    reader.onloadend = () => {
      setPreview(reader.result);
      const ext = file.type.split('/')[1] || 'jpeg';
      setFileExt(ext);
      setError('');
    };
    reader.readAsDataURL(file);
  };

  const handleFileChange = (e) => {
    const file = e.target.files?.[0];
    handleFile(file);
  };

  const handleDragOver = (e) => {
    e.preventDefault();
    e.stopPropagation();
    setDragActive(true);
  };

  const handleDragLeave = (e) => {
    e.preventDefault();
    e.stopPropagation();
    setDragActive(false);
  };

  const handleDrop = (e) => {
    e.preventDefault();
    e.stopPropagation();
    setDragActive(false);
    const file = e.dataTransfer.files?.[0];
    handleFile(file);
  };

  const handleContinue = async () => {
    if (!preview) {
      setError('Please capture or upload an image to continue');
      return;
    }

    const profileId = localStorage.getItem('profile_id') || '';
    if (!profileId) {
      setError('Session expired. Please login again.');
      navigate('/apply-now');
      return;
    }

    const getCurrentLocation = () =>
      new Promise((resolve) => {
        if (!navigator.geolocation) {
          resolve({ lat: '', lng: '' });
          return;
        }
        navigator.geolocation.getCurrentPosition(
          (position) => {
            resolve({
              lat: position.coords.latitude.toString(),
              lng: position.coords.longitude.toString(),
            });
          },
          () => resolve({ lat: '', lng: '' })
        );
      });

    const base64Data = preview.includes(',')
      ? preview.split(',')[1]
      : preview;

    setUploading(true);
    setError('');

    try {
      const location = await getCurrentLocation();
      const response = await authAPI.uploadSelfie({
        profile_id: profileId,
        file: base64Data,
        file_ext: fileExt || 'jpeg',
        geo_lat: location.lat,
        geo_long: location.lng,
      });

      if (response.Status === 1) {
        const nextEvent = response.Data?.next_step;
        if (nextEvent && eventToRouteMap[nextEvent]) {
          navigate(eventToRouteMap[nextEvent]);
        } else {
          navigate('/register-now');
        }
      } else {
        setError(response.Message || 'Failed to upload picture. Please try again.');
      }
    } catch (err) {
      setError(err.message || 'Failed to upload picture. Please try again.');
    } finally {
      setUploading(false);
    }
  };

  const videoConstraints = {
    facingMode: 'user',
  };

  const tipsSidebar = (
    <div className="bg-white border border-gray-200 rounded-2xl p-4 text-sm text-gray-600 shadow-md">
      <p className="font-semibold text-gray-900 mb-3">Tips for a perfect selfie</p>
      <ul className="list-disc list-inside space-y-2 text-gray-600">
        <li>Use good lighting and avoid backlight</li>
        <li>Remove glasses, caps or masks</li>
        <li>Ensure your face fills most of the frame</li>
        <li>PNG/JPG/JPEG up to 10 MB</li>
      </ul>
    </div>
  );

  if (checkingAuth) {
    return (
      <JourneyLayout journeyStep={3} sidebar={tipsSidebar}>
        <div className="flex items-center justify-center min-h-[400px]">
          <Loader2 className="h-8 w-8 animate-spin text-theme" />
        </div>
      </JourneyLayout>
    );
  }

  return (
    <JourneyLayout journeyStep={3} sidebar={tipsSidebar}>
      <div className="text-center space-y-3 mb-8">
        <div className="inline-flex items-center gap-2 px-3 py-1 rounded-full bg-theme/10 border border-theme/20 text-xs tracking-[0.3em] uppercase text-theme font-semibold">
          <Camera className="h-4 w-4" />
          Selfie Step
        </div>
        <h1 className="text-3xl sm:text-4xl font-bold text-gray-900">Upload your identity selfie</h1>
        <p className="text-gray-600">
          Capture directly or drop a clear selfie. We use this for instant verification.
        </p>
      </div>

      <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
        <div className="bg-gradient-to-br from-gray-50 to-white border-2 border-gray-200 rounded-2xl p-5 space-y-4 shadow-md">
          <h2 className="text-lg font-semibold flex items-center gap-2 text-gray-900">
            <Camera className="h-5 w-5 text-theme" />
            Live Capture
          </h2>
          <div className="rounded-2xl overflow-hidden border-2 border-gray-300 bg-gray-100">
            {!preview ? (
              <Webcam
                audio={false}
                ref={webcamRef}
                screenshotFormat="image/jpeg"
                videoConstraints={videoConstraints}
                className="w-full aspect-video object-cover"
              />
            ) : (
              <img src={preview} alt="Captured" className="w-full aspect-video object-cover" />
            )}
          </div>
          <button
            onClick={preview ? handleRetake : handleCapture}
            className="w-full bg-gradient-to-r from-theme to-theme2 text-white px-4 py-3 rounded-2xl font-semibold flex items-center justify-center gap-2 hover:opacity-90 transition-all shadow-md"
          >
            {preview ? (
              <>
                <RefreshCw className="h-5 w-5" />
                Retake Photo
              </>
            ) : (
              <>
                <Camera className="h-5 w-5" />
                Capture Photo
              </>
            )}
          </button>
        </div>

        <div
          className={`rounded-2xl p-5 border-2 border-dashed transition-colors ${
            dragActive
              ? 'border-theme bg-theme/5'
              : 'border-gray-300 bg-gradient-to-br from-gray-50 to-white'
          }`}
          onDragOver={handleDragOver}
          onDragLeave={handleDragLeave}
          onDrop={handleDrop}
        >
          <h2 className="text-lg font-semibold flex items-center gap-2 mb-4 text-gray-900">
            <UploadIcon className="h-5 w-5 text-theme" />
            Upload or Drag & Drop
          </h2>
          <div className="flex flex-col items-center justify-center text-center py-10 px-4 text-gray-600">
            <div className="w-16 h-16 rounded-full bg-theme/10 border-2 border-theme/20 flex items-center justify-center mb-4">
              <UploadIcon className="h-7 w-7 text-theme" />
            </div>
            <p className="font-semibold mb-2 text-gray-900">Drag & drop your image here</p>
            <p className="text-sm text-gray-500 mb-6">PNG, JPG, JPEG (max 10MB)</p>
            <button
              type="button"
              onClick={() => fileInputRef.current?.click()}
              className="bg-theme/10 border-2 border-theme/20 text-theme px-6 py-3 rounded-2xl font-semibold hover:bg-theme/20 transition-colors"
            >
              Browse Files
            </button>
            <input
              ref={fileInputRef}
              type="file"
              accept="image/*"
              className="hidden"
              onChange={handleFileChange}
            />
            {preview && (
              <div className="w-full mt-6">
                <p className="text-xs uppercase tracking-[0.3em] text-gray-500 mb-2">Preview</p>
                <img
                  src={preview}
                  alt="Selected preview"
                  className="w-full h-48 object-cover rounded-2xl border-2 border-gray-300"
                />
              </div>
            )}
          </div>
        </div>
      </div>

      {error && (
        <div className="mt-6 bg-red-50 border-2 border-red-200 rounded-2xl p-4 text-sm text-red-700">
          {error}
        </div>
      )}

      <div className="mt-6">
        <button
          onClick={handleContinue}
          disabled={uploading || !preview}
          className="w-full sm:w-auto bg-gradient-to-r from-theme to-theme2 text-white px-10 py-4 rounded-2xl font-semibold text-lg flex items-center justify-center gap-2 disabled:opacity-50 shadow-lg hover:shadow-xl transition-all"
        >
          {uploading ? (
            <>
              <Loader2 className="h-5 w-5 animate-spin" />
              <span>Uploading...</span>
            </>
          ) : (
            <>
              <span>Continue</span>
              <ArrowRight className="h-5 w-5" />
            </>
          )}
        </button>
      </div>
    </JourneyLayout>
  );
}

export default UploadPicture;

